import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getQueryEndpoint } from '@dashboard/modules/integration/data/all'
import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import { useConfigStore } from '@dashboard/stores/configStore'
import partnerEndpoints from '@dashboard/modules/integration/data/partnerEndpoints'
import { queryClient } from '@dashboard/modules/integration/services/integrationDataUtilities'

interface DataResult {
  data: any[]
  failed: boolean
}

interface DataCache {
  data: any[]
  timestamp: number
  failed: boolean
  promise?: Promise<DataResult>
}

export const useIntegrationDataStore = defineStore('integrationData', () => {
  // Cache for integration data with timestamps
  const dataCache = ref<Map<string, DataCache>>(new Map())

  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000

  // Loading states for different data types
  const loadingStates = ref<Map<string, boolean>>(new Map())

  // Helper to create a cache key
  const getCacheKey = (scope: string, key: string, teamPid: number | 'demo' | null, partnerName?: string) =>
    `${scope}:${key}:${teamPid}:${partnerName || 'none'}`

  // Check if cached data is still valid
  const isCacheValid = (cacheEntry: DataCache) => Date.now() - cacheEntry.timestamp < CACHE_DURATION

  const getDataResult = async (scope: string, key: string): Promise<DataResult> => {
    const configStore = useConfigStore()
    const integrationStore = useIntegrationStore()
    const { currentTeamPid } = configStore
    const { integratedPartner } = integrationStore

    // For partner scope, check if we have an integrated partner
    if (scope === 'partner' && !integratedPartner) {
      return { data: [], failed: false }
    }

    const cacheKey = getCacheKey(scope, key, currentTeamPid, integratedPartner?.name)
    const cached = dataCache.value.get(cacheKey)

    // Return cached data if valid
    if (cached && isCacheValid(cached)) {
      return {
        data: cached.data,
        failed: cached.failed
      }
    }

    // If there's already a fetch in progress, return that promise
    if (cached?.promise) {
      return cached.promise
    }

    // Start loading
    loadingStates.value.set(cacheKey, true)

    // Create the fetch promise
    const fetchPromise = (async (): Promise<DataResult> => {
      try {
        // Check if the endpoint exists
        const endpoint = getQueryEndpoint(scope, key)
        if (!endpoint) {
          console.warn(`Integration endpoint not found: ${scope}/${key}`)
          return { data: [], failed: false }
        }

        // Call the query function
        const result = await endpoint.queryFn(currentTeamPid as number)
        const data = result || []

        // Update cache with the result
        dataCache.value.set(cacheKey, {
          data,
          timestamp: Date.now(),
          failed: false
        })

        return { data, failed: false }
      } catch (error) {
        console.error(`Error fetching integration data for ${scope}/${key}:`, error)

        // Cache empty result on error to prevent repeated failed requests
        dataCache.value.set(cacheKey, {
          data: [],
          timestamp: Date.now(),
          failed: true
        })

        return { data: [], failed: true }
      } finally {
        loadingStates.value.set(cacheKey, false)
      }
    })()

    // Store the promise in cache to prevent duplicate requests
    if (cached) {
      cached.promise = fetchPromise
    } else {
      dataCache.value.set(cacheKey, {
        data: [],
        timestamp: 0,
        failed: false,
        promise: fetchPromise
      })
    }

    return fetchPromise
  }

  // Main function to get integration data
  const getData = async (scope: string, key: string) => {
    const result = await getDataResult(scope, key)
    return result.data
  }

  // Get native fields
  const getNativeFields = (includeNative: boolean = true) => {
    if (!includeNative) {
      return Promise.resolve([])
    }
    return getData('partner', 'native_fields')
  }

  // Get custom fields
  const getCustomFields = () => getData('partner', 'custom_fields')

  const getCustomFieldsResult = () => getDataResult('partner', 'custom_fields')

  // Get tags
  const getTags = () => getData('partner', 'tags')

  const getTagsResult = () => getDataResult('partner', 'tags')

  // Get lists
  const getLists = () => getData('partner', 'lists')

  const getListsResult = () => getDataResult('partner', 'lists')

  const createCustomField = async (teamPid: number, label: string) => {
    const { integratedPartner } = useIntegrationStore()
    const cacheKey = getCacheKey('partner', 'custom_fields', teamPid, integratedPartner?.name)
    const response = await partnerEndpoints.createCustomField.mutationFn(teamPid, label)

    if (response.data?.id) {
      dataCache.value.delete(cacheKey)
      await queryClient.invalidateQueries({
        queryKey: ['integrations', teamPid, 'partner', 'custom_fields'],
        exact: true,
        refetchType: 'all'
      })
    }

    return response
  }

  // Clear cache for specific data or all data
  const clearCache = (scope?: string, key?: string) => {
    if (scope && key) {
      const configStore = useConfigStore()
      const integrationStore = useIntegrationStore()
      const cacheKey = getCacheKey(scope, key, configStore.currentTeamPid, integrationStore.integratedPartner?.name)
      dataCache.value.delete(cacheKey)
    } else {
      dataCache.value.clear()
    }
  }

  // Get loading state for specific data
  const isLoading = (scope: string, key: string) => {
    const configStore = useConfigStore()
    const integrationStore = useIntegrationStore()
    const cacheKey = getCacheKey(scope, key, configStore.currentTeamPid, integrationStore.integratedPartner?.name)
    return loadingStates.value.get(cacheKey) || false
  }

  return {
    // Methods
    getData,
    getDataResult,
    getNativeFields,
    getCustomFields,
    getCustomFieldsResult,
    getTags,
    getTagsResult,
    getLists,
    getListsResult,
    createCustomField,
    clearCache,
    isLoading,

    // State (read-only)
    dataCache: computed(() => dataCache.value),
    loadingStates: computed(() => loadingStates.value)
  }
})
